import { NavLink, Outlet, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import {
  Home, Users, Calendar as CalIcon, ListChecks, ChefHat, ShoppingCart,
  MessageSquare, Image as ImgIcon, Sparkles, Phone, Wrench, MapPin,
  DollarSign, FileLock2, Target, Trophy, Siren, PawPrint, BookOpen,
  Gift, LogOut, Menu, X, User as UserIcon
} from "lucide-react";
import { useState } from "react";

// Navigation grouped for visual rhythm in the sidebar.
const navGroups = [
  {
    label: "Today",
    items: [
      { to: "/", label: "Home", icon: Home },
      { to: "/calendar", label: "Calendar", icon: CalIcon },
      { to: "/tasks", label: "Tasks", icon: ListChecks },
      { to: "/messages", label: "Messages", icon: MessageSquare },
      { to: "/ai", label: "AI Assistant", icon: Sparkles },
    ],
  },
  {
    label: "The Kitchen",
    items: [
      { to: "/recipes", label: "Recipes", icon: ChefHat },
      { to: "/shopping", label: "Shopping", icon: ShoppingCart },
      { to: "/pets", label: "Pets", icon: PawPrint },
    ],
  },
  {
    label: "Memories",
    items: [
      { to: "/journal", label: "Journal", icon: BookOpen },
      { to: "/photos", label: "Photos", icon: ImgIcon },
      { to: "/wishlists", label: "Wishlists", icon: Gift },
      { to: "/goals", label: "Goals", icon: Target },
      { to: "/chores", label: "Chores", icon: Trophy },
    ],
  },
  {
    label: "The House",
    items: [
      { to: "/members", label: "Members", icon: Users },
      { to: "/contacts", label: "Contacts", icon: Phone },
      { to: "/maintenance", label: "Maintenance", icon: Wrench },
      { to: "/tracker", label: "Tracker", icon: MapPin },
      { to: "/budget", label: "Budget", icon: DollarSign },
      { to: "/documents", label: "Documents", icon: FileLock2 },
      { to: "/emergency", label: "Emergency", icon: Siren },
    ],
  },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const familyName = user?.family?.name || "Family";
  const memberName = user?.member?.name || "Member";
  const initials = memberName.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen flex" style={{ background: "var(--hh-cream)" }}>
      {/* Mobile menu button */}
      <button
        onClick={() => setOpen(!open)}
        className="lg:hidden fixed top-4 left-4 z-[60] nb-btn nb-btn-ink p-2.5"
        data-testid="mobile-menu-toggle"
      >
        {open ? <X strokeWidth={2.5} className="w-4 h-4" /> : <Menu strokeWidth={2.5} className="w-4 h-4" />}
      </button>

      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky top-0 left-0 h-screen w-72 z-50 transition-transform duration-300 ease-out flex flex-col ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        style={{
          background: "var(--hh-card-soft)",
          borderRight: "1px solid var(--hh-line)",
        }}
        data-testid="sidebar"
      >
        {/* Brand */}
        <div className="p-6 pt-7" style={{ borderBottom: "1px solid var(--hh-line)" }}>
          <Link to="/" className="flex items-center gap-3 group">
            <div
              className="w-11 h-11 rounded-2xl flex items-center justify-center transition-transform group-hover:scale-[1.04]"
              style={{
                background: "var(--hh-terracotta)",
                color: "var(--hh-card)",
                fontFamily: "var(--hh-display)",
                fontWeight: 800,
                fontSize: "1.25rem",
                boxShadow: "0 8px 18px -10px rgba(199,93,63,0.7)",
              }}
            >
              f
            </div>
            <div>
              <div
                className="font-extrabold text-xl leading-none"
                style={{ fontFamily: "var(--hh-display)", color: "var(--hh-ink)" }}
              >
                familytable
              </div>
              <p
                className="text-[11px] mt-1 truncate max-w-[160px] uppercase tracking-wider"
                style={{ color: "var(--hh-ink-soft)" }}
                data-testid="family-name"
              >
                The {familyName}
              </p>
            </div>
          </Link>
        </div>

        {/* Nav groups */}
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-5">
          {navGroups.map((group) => (
            <div key={group.label}>
              <div
                className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em]"
                style={{ color: "var(--hh-ink-soft)", opacity: 0.7 }}
              >
                {group.label}
              </div>
              <div className="space-y-0.5">
                {group.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === "/"}
                    onClick={() => setOpen(false)}
                    data-testid={`nav-${item.label.toLowerCase().replace(" ", "-")}`}
                    className={({ isActive }) =>
                      `relative flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                        isActive ? "is-active" : "hover:bg-[rgba(42,31,23,0.04)]"
                      }`
                    }
                    style={({ isActive }) => ({
                      color: isActive ? "var(--hh-ink)" : "var(--hh-ink-soft)",
                      background: isActive ? "var(--hh-card)" : "transparent",
                      boxShadow: isActive
                        ? "0 1px 0 rgba(42,31,23,0.04), 0 12px 24px -16px rgba(42,31,23,0.20)"
                        : "none",
                      border: isActive ? "1px solid var(--hh-line)" : "1px solid transparent",
                    })}
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && (
                          <span
                            className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full"
                            style={{ background: "var(--hh-terracotta)" }}
                          />
                        )}
                        <item.icon
                          strokeWidth={isActive ? 2.2 : 1.8}
                          className="w-[18px] h-[18px] shrink-0"
                          style={{ color: isActive ? "var(--hh-terracotta)" : "currentColor" }}
                        />
                        <span style={{ fontWeight: isActive ? 600 : 500 }}>{item.label}</span>
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Footer profile */}
        <div className="p-3" style={{ borderTop: "1px solid var(--hh-line)" }}>
          <div className="flex items-center gap-3 p-2 rounded-2xl">
            <Link
              to="/profile"
              onClick={() => setOpen(false)}
              className="w-10 h-10 rounded-full flex items-center justify-center font-bold transition-transform hover:scale-105"
              style={{
                backgroundColor: user?.member?.avatar_color || "var(--hh-honey-soft)",
                color: "var(--hh-ink)",
                fontFamily: "var(--hh-display)",
                fontSize: "0.95rem",
                boxShadow: "inset 0 0 0 1px rgba(42,31,23,0.10), 0 6px 14px -8px rgba(42,31,23,0.25)",
              }}
              data-testid="user-avatar"
              title="My Profile"
            >
              {initials}
            </Link>
            <Link
              to="/profile"
              onClick={() => setOpen(false)}
              className="flex-1 min-w-0"
              data-testid="user-profile-link"
            >
              <p className="font-semibold text-sm truncate" data-testid="user-name" style={{ color: "var(--hh-ink)" }}>
                {memberName}
              </p>
              <p className="text-xs truncate" style={{ color: "var(--hh-ink-soft)" }}>
                {user?.member?.role}
              </p>
            </Link>
            <button
              onClick={logout}
              className="nb-btn p-2"
              data-testid="logout-btn"
              title="Sign out"
              aria-label="Sign out"
            >
              <LogOut strokeWidth={2.2} className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Backdrop on mobile */}
      {open && (
        <div
          className="lg:hidden fixed inset-0 z-40"
          style={{ background: "rgba(42,31,23,0.45)", backdropFilter: "blur(2px)" }}
          onClick={() => setOpen(false)}
        />
      )}

      <main className="flex-1 min-w-0">
        <div className="p-6 lg:p-12 pt-20 lg:pt-12 max-w-[1200px] mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
